// @flow

import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { i18n, Analytics, ADDRESS_COUNTRIES, getAddressFieldConfig } from 'shared/utils';
import { createFragmentContainer, graphql } from 'react-relay';
import * as Actions from 'main-app/store/Actions';
import AddCustomerMutation from 'main-app/mutations/AddCustomer';
import UpdateCustomerMutation from 'main-app/mutations/UpdateCustomer';
import AddCustomerAddressMutation from 'main-app/mutations/AddCustomerAddress';
import UpdateCustomerAddressMutation from 'main-app/mutations/UpdateCustomerAddress';
import DeleteCustomerAddressMutation from 'main-app/mutations/DeleteCustomerAddress';
import Button from 'shared/components/common/Button';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';
import Divider from 'shared/components/common/Divider';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
  SelectUserField,
} from 'shared/components/form';
import type { AddUpdateCustomerModal_customer as CustomerFragment } from './__generated__/AddUpdateCustomerModal_customer';

type Props = {
  onClose: () => void,
  onSuccess?: Object => void,
  onReopen?: () => void,
  createAnotherCustomer?: boolean,
  customer: ?CustomerFragment,
};

const LANGUAGE = {
  update: {
    title: i18n.t('Edit Customer'),
    submitButton: i18n.t('Update Customer'),
    successTitle: i18n.t('Customer Successfully Updated'),
    successBody: userAssigned =>
      userAssigned && i18n.t('The salesperson has been notified.'),
  },
  add: {
    title: i18n.t('Create Customer'),
    submitButton: i18n.t('Create Customer'),
    successTitle: i18n.t('Customer Successfully Created'),
    successBody: userAssigned =>
      userAssigned
        ? i18n.t('The salesperson has been notified. Now create a Order!')
        : i18n.t('Now create a Order!'),
  },
};

const copyShippingToBillingAddress = (values, setFieldValue) => {
  const def =
    (values.shippingAddresses || []).find(a => a.isDefault) ||
    (values.shippingAddresses || [])[0];

  if (!def) {
    return;
  }

  setFieldValue('billingAddressLine1', def.addressLine1 || '');
  setFieldValue('billingAddressLine2', def.addressLine2 || '');
  setFieldValue('billingAddressLine3', def.addressLine3 || '');
  setFieldValue('billingAddressCity', def.addressCity || '');
  setFieldValue('billingAddressState', def.addressState || '');
  setFieldValue('billingAddressZip', def.addressZip || '');
};

const COUNTRY_OPTIONS = ADDRESS_COUNTRIES.map(c => ({ value: c.code, label: c.label }));

const blankShippingAddress = (isDefault = false) => ({
  id: null,
  label: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  addressCountry: COUNTRY_OPTIONS.find(c => c.value === 'US') || null,
  isDefault,
  _deleted: false,
});

const buildInitialAddresses = customer => {
  const list =
    (customer && customer.addresses && customer.addresses.slice()) || [];

  if (list.length > 0) {
    return list.map(a => ({
      id: a.id,
      label: a.label || '',
      addressLine1: a.addressLine1 || '',
      addressLine2: a.addressLine2 || '',
      addressLine3: a.addressLine3 || '',
      addressCity: a.addressCity || '',
      addressState: a.addressState || '',
      addressZip: a.addressZip || '',
      addressCountry: COUNTRY_OPTIONS.find(c => c.value === (a.addressCountry || 'US')) || COUNTRY_OPTIONS.find(c => c.value === 'US') || null,
      isDefault: !!a.isDefault,
      _deleted: false,
    }));
  }

  // New customer or legacy customer with no normalized addresses yet —
  // start with a single default-marked blank.
  return [blankShippingAddress(true)];
};

const AddressCardWrap = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fafbfc;
`;

const AddressCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const AddressCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
`;

const DefaultPill = styled.span`
  background: #3b82f6;
  color: #fff;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const RemoveBtn = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;
  &:hover {
    text-decoration: underline;
  }
`;

const SetDefaultBtn = styled.button.attrs({ type: 'button' })`
  background: none;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  &:hover {
    background: #eff6ff;
  }
`;

const AddAddressBtn = styled.button.attrs({ type: 'button' })`
  background: #fff;
  border: 1px dashed #3b82f6;
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 16px;
  border-radius: 8px;
  width: 100%;
  margin-bottom: 8px;
  &:hover {
    background: #eff6ff;
  }
`;

const AddUpdateCustomerModal = ({
  onClose,
  onSuccess,
  onReopen,
  createAnotherCustomer,
  customer,
}: Props) => {
  const language = LANGUAGE[customer ? 'update' : 'add'];

  return (
    <Modal maxWidth={700}>
      <Formik
        initialValues={{
          createAnotherCustomer,
          name: customer?.name || '',
          primaryContactName: customer?.primaryContactName || '',
          secondaryContactName: customer?.secondaryContactName || '',
          shippingAddresses: buildInitialAddresses(customer),
          billingAddressLine1: customer?.billingAddressLine1 || '',
          billingAddressLine2: customer?.billingAddressLine2 || '',
          billingAddressLine3: customer?.billingAddressLine3 || '',
          billingAddressCity: customer?.billingAddressCity || '',
          billingAddressState: customer?.billingAddressState || '',
          billingAddressZip: customer?.billingAddressZip || '',
          email: customer?.email || '',
          phone: customer?.phone || '',
          phone2: customer?.phone2 || '',
          fax: customer?.fax || '',
          salesUser: customer?.salesUser && {
            value: customer?.salesUser?.id,
            data: customer?.salesUser,
          },
          notes: customer?.notes || '',
        }}
        validationSchema={Yup.object().shape({
          createAnotherCustomer: Yup.boolean(),
          name: Yup.string().required('Required'),
          email: Yup.string().email('Invalid Email'),
          billingAddressZip: Yup.string(),
          salesUser: Yup.object().nullable(),
          shippingAddresses: Yup.array().of(
            Yup.object().shape({
              label: Yup.string(),
              addressLine1: Yup.string(),
              addressCity: Yup.string(),
              addressState: Yup.string(),
              addressZip: Yup.string(),
            }),
          ),
        })}
        onSubmit={async (values: *, { setSubmitting }: *) => {
          setSubmitting(true);

          const mutation = customer
            ? UpdateCustomerMutation
            : AddCustomerMutation;

          // For backwards compat, also write the default shipping address
          // into the legacy customer columns (so older code paths and
          // reports keep working until those columns are dropped).
          const activeAddresses = (values.shippingAddresses || []).filter(
            a => !a._deleted,
          );
          const defaultAddr =
            activeAddresses.find(a => a.isDefault) || activeAddresses[0];

          const input: any = {
            name: values.name,
            primaryContactName: values.primaryContactName,
            secondaryContactName: values.secondaryContactName,
            shippingAddressLine1: defaultAddr?.addressLine1 || '',
            shippingAddressLine2: defaultAddr?.addressLine2 || '',
            shippingAddressLine3: defaultAddr?.addressLine3 || '',
            shippingAddressCity: defaultAddr?.addressCity || '',
            shippingAddressState: defaultAddr?.addressState || '',
            shippingAddressZip: defaultAddr?.addressZip || '',
            billingAddressLine1: values.billingAddressLine1,
            billingAddressLine2: values.billingAddressLine2,
            billingAddressLine3: values.billingAddressLine3,
            billingAddressCity: values.billingAddressCity,
            billingAddressState: values.billingAddressState,
            billingAddressZip: values.billingAddressZip,
            email: values.email || null,
            phone: values.phone,
            phone2: values.phone2,
            fax: values.fax,
            salesUserId: values?.salesUser?.value || null,
            notes: values.notes,
          };

          if (customer) {
            input.id = customer.id;
          }

          try {
            const response = await mutation.commit({
              variables: {
                input,
              },
            });

            const newCustomer = (
              response.updateCustomer || response.addCustomer
            ).customerEdge.node;

            // Reconcile shipping addresses against the customer_address table.
            const customerId = newCustomer.id;
            const allAddrs = values.shippingAddresses || [];

            // Deletes
            for (const addr of allAddrs) {
              if (addr.id && addr._deleted) {
                // eslint-disable-next-line no-await-in-loop
                await DeleteCustomerAddressMutation.commit({
                  variables: { input: { id: addr.id } },
                });
              }
            }

            // Updates and creates
            for (const addr of allAddrs) {
              if (addr._deleted) {
                continue;
              }

              // Skip rows that have nothing filled in (e.g. a freshly-added
              // blank that the user didn't touch).
              const hasContent =
                addr.label ||
                addr.addressLine1 ||
                addr.addressCity ||
                addr.addressZip;
              if (!hasContent) {
                continue;
              }

              const payload = {
                label: addr.label || 'Address',
                addressLine1: addr.addressLine1 || null,
                addressLine2: addr.addressLine2 || null,
                addressLine3: addr.addressLine3 || null,
                addressCity: addr.addressCity || null,
                addressState: addr.addressState || null,
                addressZip: addr.addressZip || null,
                addressCountry: (addr.addressCountry && typeof addr.addressCountry === 'object' ? addr.addressCountry.value : addr.addressCountry) || 'US',
                isDefault: !!addr.isDefault,
              };

              if (addr.id) {
                // eslint-disable-next-line no-await-in-loop
                await UpdateCustomerAddressMutation.commit({
                  variables: { input: { id: addr.id, ...payload } },
                });
              } else {
                // eslint-disable-next-line no-await-in-loop
                await AddCustomerAddressMutation.commit({
                  variables: { input: { customerId, ...payload } },
                });
              }
            }

            if (values.createAnotherCustomer && onReopen) {
              onReopen();
            } else {
              onClose();

              if (onSuccess) {
                onSuccess(newCustomer);
              }
            }

            Analytics.trackEvent(`${customer ? 'Update' : 'Create'} Customer`, {
              customerName: values.name,
              billingCity: values.billingAddressCity,
              billingState: values.billingAddressState,
              salesperson:
                values.salesUser?.data?.firstName +
                ' ' +
                values.salesUser?.data?.lastName,
            });
            Actions.alertNotification(
              {
                title: language.successTitle,
                body: language.successBody(values.salesUser?.value),
              },
              'success',
            );
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'error');
          }
        }}
        render={({
          values,
          errors,
          isValid,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="name"
                    label={i18n.t('Customer')}
                    placeholder={i18n.t('Customer Name')}
                    error={errors.name}
                  />
                }
                right={
                  <SelectUserField
                    name="salesUser"
                    label={i18n.t('Salesperson')}
                    clearable
                    error={errors.salesUser}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="primaryContactName"
                    label={i18n.t('Primary Contact')}
                    placeholder={i18n.t('Primary Contact')}
                    error={errors.primaryContactName}
                  />
                }
                right={
                  <FieldGroup
                    name="secondaryContactName"
                    label={i18n.t('Secondary Contact')}
                    placeholder={i18n.t('Secondary Contact')}
                    error={errors.secondaryContactName}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="email"
                    label={i18n.t('Email Address')}
                    placeholder={i18n.t('Enter Email')}
                    error={errors.email}
                  />
                }
                right={
                  <FieldGroup
                    name="phone"
                    label={i18n.t('Phone Number')}
                    placeholder={i18n.t('Enter Phone Number')}
                    error={errors.phone}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="phone2"
                    label={i18n.t('Secondary Phone Number')}
                    placeholder={i18n.t('Enter Phone Number')}
                    error={errors.phone2}
                  />
                }
                right={
                  <FieldGroup
                    name="fax"
                    label={i18n.t('Fax')}
                    placeholder={i18n.t('Enter Fax Number')}
                    error={errors.fax}
                  />
                }
              />
              <Divider style={{ marginTop: 8, marginBottom: 24 }} />
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  marginBottom: 12,
                  color: '#374151',
                }}
              >
                {i18n.t('Shipping Addresses')}
              </div>
              {(values.shippingAddresses || [])
                .map((addr, idx) => ({ addr, idx }))
                .filter(({ addr }) => !addr._deleted)
                .map(({ addr, idx }) => {
                  const visibleCount = (values.shippingAddresses || []).filter(
                    a => !a._deleted,
                  ).length;
                  return (
                    <AddressCardWrap key={`addr-${idx}`}>
                      <AddressCardHeader>
                        <AddressCardTitle>
                          <span>
                            {addr.label ||
                              `${i18n.t('Address')} ${idx + 1}`}
                          </span>
                          {addr.isDefault && (
                            <DefaultPill>{i18n.t('DEFAULT')}</DefaultPill>
                          )}
                        </AddressCardTitle>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {!addr.isDefault && (
                            <SetDefaultBtn
                              onClick={() => {
                                const next = (
                                  values.shippingAddresses || []
                                ).map((a, i) => ({
                                  ...a,
                                  isDefault: i === idx,
                                }));
                                setFieldValue('shippingAddresses', next);
                              }}
                            >
                              {i18n.t('Set as Default')}
                            </SetDefaultBtn>
                          )}
                          {visibleCount > 1 && (
                            <RemoveBtn
                              onClick={() => {
                                const list = (
                                  values.shippingAddresses || []
                                ).slice();

                                if (list[idx].id) {
                                  list[idx] = {
                                    ...list[idx],
                                    _deleted: true,
                                    isDefault: false,
                                  };
                                } else {
                                  list.splice(idx, 1);
                                }

                                // If we just removed the default, promote
                                // the first remaining visible address.
                                const stillVisible = list.filter(
                                  a => !a._deleted,
                                );
                                if (
                                  stillVisible.length > 0 &&
                                  !stillVisible.some(a => a.isDefault)
                                ) {
                                  const promoteIdx = list.findIndex(
                                    a => !a._deleted,
                                  );
                                  list[promoteIdx] = {
                                    ...list[promoteIdx],
                                    isDefault: true,
                                  };
                                }

                                setFieldValue('shippingAddresses', list);
                              }}
                            >
                              {i18n.t('Remove')}
                            </RemoveBtn>
                          )}
                        </div>
                      </AddressCardHeader>
                      <FieldGroup
                        name={`shippingAddresses[${idx}].label`}
                        label={i18n.t('Label')}
                        placeholder={i18n.t('e.g. Main Office, Warehouse')}
                      />
                      <FieldGroup
                        name={`shippingAddresses[${idx}].addressCountry`}
                        label={i18n.t('Country')}
                        type="select"
                        options={COUNTRY_OPTIONS}
                        placeholder={i18n.t('Select a country...')}
                        isSearchable
                      />
                      <AddressAutocomplete
                        value={(values.shippingAddresses[idx] && values.shippingAddresses[idx].addressLine1) || ''}
                        label={i18n.t('Shipping Address')}
                        placeholder={i18n.t('Shipping Address Line 1')}
                        onChange={val => setFieldValue(`shippingAddresses[${idx}].addressLine1`, val)}
                        onAddressSelect={({ line1, city, state, zip, country }) => {
                          if (line1) setFieldValue(`shippingAddresses[${idx}].addressLine1`, line1);
                          if (city) setFieldValue(`shippingAddresses[${idx}].addressCity`, city);
                          if (state) setFieldValue(`shippingAddresses[${idx}].addressState`, state);
                          if (zip) setFieldValue(`shippingAddresses[${idx}].addressZip`, zip);
                          if (country) setFieldValue(`shippingAddresses[${idx}].addressCountry`, country);
                        }}
                      />
                      <FieldGroupRow
                        left={
                          <FieldGroup
                            name={`shippingAddresses[${idx}].addressLine2`}
                            label={i18n.t('Address 2')}
                            placeholder={i18n.t('Shipping Address Line 2')}
                          />
                        }
                        right={
                          <FieldGroup
                            name={`shippingAddresses[${idx}].addressLine3`}
                            label={i18n.t('Address 3')}
                            placeholder={i18n.t('Shipping Address Line 3')}
                          />
                        }
                      />
                      <FieldGroupRow
                        left={
                          <FieldGroup
                            name={`shippingAddresses[${idx}].addressCity`}
                            label={i18n.t('City')}
                            placeholder={i18n.t('City')}
                          />
                        }
                        center={
                          <FieldGroup
                            name={`shippingAddresses[${idx}].addressState`}
                            label={i18n.t(getAddressFieldConfig((values.shippingAddresses[idx] || {}).addressCountry).stateLabel)}
                            placeholder={i18n.t(getAddressFieldConfig((values.shippingAddresses[idx] || {}).addressCountry).statePlaceholder)}
                          />
                        }
                        right={
                          <FieldGroup
                            name={`shippingAddresses[${idx}].addressZip`}
                            label={i18n.t(getAddressFieldConfig((values.shippingAddresses[idx] || {}).addressCountry).zipLabel)}
                            placeholder={i18n.t(getAddressFieldConfig((values.shippingAddresses[idx] || {}).addressCountry).zipPlaceholder)}
                          />
                        }
                      />
                    </AddressCardWrap>
                  );
                })}
              <AddAddressBtn
                onClick={() => {
                  const list = (values.shippingAddresses || []).slice();
                  const visibleCount = list.filter(a => !a._deleted).length;
                  list.push(blankShippingAddress(visibleCount === 0));
                  setFieldValue('shippingAddresses', list);
                }}
              >
                + {i18n.t('Add Another Shipping Address')}
              </AddAddressBtn>
              <Divider style={{ marginTop: 8, marginBottom: 24 }} />
              <FieldGroup
                label={i18n.t('Same as Shipping Address')}
                onChange={value => {
                  if (value) {
                    copyShippingToBillingAddress(values, setFieldValue);
                  }
                }}
                style={{ marginBottom: 16 }}
                type="toggle"
                sideLabel="right"
                rawField
              />
              <AddressAutocomplete
                value={values.billingAddressLine1 || ''}
                label={i18n.t('Billing Address')}
                placeholder={i18n.t('Billing Address Line 1')}
                error={errors.billingAddressLine1}
                onChange={val => setFieldValue('billingAddressLine1', val)}
                onAddressSelect={({ line1, city, state, zip }) => {
                  if (line1) setFieldValue('billingAddressLine1', line1);
                  if (city) setFieldValue('billingAddressCity', city);
                  if (state) setFieldValue('billingAddressState', state);
                  if (zip) setFieldValue('billingAddressZip', zip);
                }}
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="billingAddressLine2"
                    label={i18n.t('Address 2')}
                    placeholder={i18n.t('Billing Address Line 2')}
                    error={errors.billingAddressLine2}
                  />
                }
                right={
                  <FieldGroup
                    name="billingAddressLine3"
                    label={i18n.t('Address 3')}
                    placeholder={i18n.t('Billing Address Line 3')}
                    error={errors.billingAddressLine3}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="billingAddressCity"
                    label={i18n.t('City')}
                    placeholder={i18n.t('City')}
                    error={errors.billingAddressCity}
                  />
                }
                center={
                  <FieldGroup
                    name="billingAddressState"
                    label={i18n.t('State')}
                    placeholder={i18n.t('State')}
                    error={errors.billingAddressState}
                  />
                }
                right={
                  <FieldGroup
                    name="billingAddressZip"
                    label={i18n.t('Postal Code')}
                    placeholder={i18n.t('Postal Code')}
                    error={errors.billingAddressZip}
                  />
                }
              />
              <Divider style={{ marginTop: 8, marginBottom: 24 }} />
              <FieldGroup
                name="notes"
                type="textarea"
                label={i18n.t('Notes')}
                placeholder={i18n.t('Enter notes here...')}
                error={errors.notes}
              />
            </ModalBody>
            <ModalFooter>
              {!customer && (
                <FieldGroup
                  label={i18n.t('Create Another Customer')}
                  sideLabel="right"
                  name="createAnotherCustomer"
                  error={errors.createAnotherCustomer}
                  type="toggle"
                />
              )}
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {language.submitButton}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

AddUpdateCustomerModal.defaultProps = {
  onSuccess: undefined,
  onReopen: undefined,
  createAnotherCustomer: false,
};

export default createFragmentContainer(AddUpdateCustomerModal, {
  customer: graphql`
    fragment AddUpdateCustomerModal_customer on Customer {
      id
      name
      primaryContactName
      secondaryContactName
      addresses {
        id
        label
        addressLine1
        addressLine2
        addressLine3
        addressCity
        addressState
        addressZip
        addressCountry
        isDefault
      }
      billingAddressLine1
      billingAddressLine2
      billingAddressLine3
      billingAddressCity
      billingAddressState
      billingAddressZip
      email
      phone
      phone2
      fax
      notes
      salesUser {
        id
        firstName
        lastName
      }
    }
  `,
});
